import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useNavigate, Link } from "react-router-dom";
import { BookOpen, Target, TrendingUp, Users, Gamepad2, Brain, ArrowRight } from "lucide-react";

const Resources = () => {
  const navigate = useNavigate();

  const resourcesSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Gamification Resources & Best Practices",
    "description": "Learn about gamification strategies, engagement mechanics, and best practices for mobile apps.",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": [
        {
          "@type": "Article",
          "position": 1,
          "name": "What is App Gamification?",
          "description": "Understanding the fundamentals of gamification and how it drives user engagement.",
        },
        {
          "@type": "Article",
          "position": 2,
          "name": "Core Gamification Mechanics",
          "description": "Explore essential game mechanics like points, badges, leaderboards, and progress tracking.",
        },
        {
          "@type": "Article",
          "position": 3,
          "name": "Gamification by App Category",
          "description": "Category-specific strategies for education, health, productivity, and social apps.",
        },
      ],
    },
  };

  const articles = [
    {
      icon: BookOpen,
      title: "What is App Gamification?",
      description: "Gamification is the application of game-design elements and principles in non-game contexts. For mobile apps, this means incorporating mechanics like points, badges, challenges, and rewards to increase user engagement, motivation, and retention.",
      keyPoints: [
        "Increases daily active users by up to 145%",
        "Improves user retention rates by 30-50%",
        "Boosts in-app purchases and conversions",
        "Creates emotional connections with your app",
      ],
    },
    {
      icon: Target,
      title: "Core Gamification Mechanics",
      description: "Effective gamification relies on proven game mechanics that tap into human psychology. Understanding these mechanics helps you choose the right elements for your specific app and audience.",
      keyPoints: [
        "Points & Scoring: Quantify progress and achievements",
        "Badges & Achievements: Recognize milestones and behaviors",
        "Leaderboards: Foster competition and social proof",
        "Progress Bars: Visualize advancement toward goals",
        "Challenges & Quests: Create meaningful objectives",
        "Rewards & Unlocks: Incentivize continued engagement",
      ],
    },
    {
      icon: TrendingUp,
      title: "Engagement Tiers Explained",
      description: "Not every app needs the same level of gamification. Understanding the three engagement tiers helps you implement the right amount of game mechanics for your specific use case.",
      keyPoints: [
        "Core Tier: Basic progress tracking and achievements for utility apps",
        "Moderate Tier: Social features and challenges for lifestyle apps",
        "High Tier: Full game loops with narratives for entertainment and fitness apps",
      ],
    },
    {
      icon: Users,
      title: "Gamification by App Category",
      description: "Different app categories benefit from different gamification strategies. Here's how top apps in each category use game mechanics:",
      keyPoints: [
        "Education: Duolingo uses streaks, XP, and leagues",
        "Fitness: Nike Run Club uses challenges and achievements",
        "Productivity: Habitica turns tasks into RPG quests",
        "Finance: Qapital uses goal visualization and rewards",
        "Social: Instagram uses engagement metrics as game scores",
      ],
    },
    {
      icon: Brain,
      title: "Psychology Behind Gamification",
      description: "Successful gamification leverages core psychological principles to drive behavior and create lasting habits.",
      keyPoints: [
        "Intrinsic Motivation: Autonomy, mastery, and purpose",
        "Variable Rewards: Unpredictable rewards increase engagement",
        "Loss Aversion: Streak protection motivates consistency",
        "Social Proof: Seeing others succeed drives action",
        "Dopamine Loops: Small wins create addictive feedback cycles",
      ],
    },
    {
      icon: Gamepad2,
      title: "Implementation Best Practices",
      description: "Implementing gamification effectively requires careful planning and iteration. Follow these best practices to maximize impact:",
      keyPoints: [
        "Start small: Begin with 2-3 core mechanics",
        "Align with goals: Gamify behaviors that matter",
        "Test and iterate: A/B test different reward structures",
        "Avoid over-gamification: Too many elements overwhelm users",
        "Measure impact: Track engagement metrics continuously",
      ],
    },
  ];

  return (
    <>
      <SEO
        title="Gamification Resources & Best Practices"
        description="Learn about gamification strategies, game mechanics, and best practices for increasing app engagement. Free resources from gamification experts."
        canonicalUrl="https://gamifyme-scorecard.lovable.app/resources"
        keywords="gamification guide, app gamification tutorial, game mechanics, user engagement strategies, gamification best practices, how to gamify app"
        jsonLd={resourcesSchema}
      />
      <div className="min-h-screen bg-gradient-to-b from-background via-primary/5 to-background">
        <Navigation />
        <Breadcrumbs />

        <main>
          {/* Hero Section */}
          <section className="pt-32 pb-12 px-4" aria-labelledby="resources-heading">
            <header className="max-w-4xl mx-auto text-center space-y-6">
              <h1 id="resources-heading" className="text-5xl md:text-6xl font-bold">
                Gamification Resources
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Learn the fundamentals of app gamification and discover strategies used by the world's most engaging apps
              </p>
            </header>
          </section>

          {/* Articles Grid */}
          <section className="py-12 px-4" aria-label="Gamification articles">
            <div className="max-w-6xl mx-auto space-y-8">
              {articles.map((article, index) => {
                const IconComponent = article.icon;
                return (
                  <article
                    key={index}
                    className="group"
                    itemScope
                    itemType="https://schema.org/Article"
                  >
                    <Card className="p-8 md:p-10 shadow-lg hover:shadow-xl transition-shadow border-2 border-transparent hover:border-primary/20">
                      <div className="flex flex-col md:flex-row gap-6">
                        <div className="flex-shrink-0">
                          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                            <IconComponent className="w-8 h-8 text-primary" />
                          </div>
                        </div>
                        <div className="flex-1 space-y-4">
                          <h2 
                            className="text-2xl md:text-3xl font-bold group-hover:text-primary transition-colors"
                            itemProp="headline"
                          >
                            {article.title}
                          </h2>
                          <p 
                            className="text-lg text-muted-foreground"
                            itemProp="description"
                          >
                            {article.description}
                          </p>
                          <ul className="grid sm:grid-cols-2 gap-3 pt-2">
                            {article.keyPoints.map((point, pointIndex) => (
                              <li 
                                key={pointIndex}
                                className="flex items-start gap-2 text-muted-foreground"
                              >
                                <ArrowRight className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                                <span>{point}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </Card>
                  </article>
                );
              })}
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 px-4 bg-gradient-to-b from-primary/5 to-background">
            <div className="max-w-4xl mx-auto text-center space-y-8">
              <h2 className="text-4xl md:text-5xl font-bold">
                Ready for Your Personalized Strategy?
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Stop researching and start implementing. Get a customized gamification strategy for your specific app in just 10 minutes.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  size="lg"
                  onClick={() => navigate("/pricing")}
                  className="text-lg px-10 py-6 bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity shadow-lg"
                >
                  Get Your Strategy - $79
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate("/how-it-works")}
                  className="text-lg px-10 py-6"
                >
                  Learn How It Works
                </Button>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Resources;